import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Autocomplete, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import OrderAutocomplete, { OrderAutocompleteItem } from '@/modules/commons/components/OrderAutocomplete';

import { InventoryIssueFormData, InventoryIssueFormLine } from '../types';
import IssueLinesTable from './IssueLinesTable';

interface IssueFormProps {
  formData: InventoryIssueFormData;
  statusOptions: Array<{ id: string; name: string; color?: string }>;
  warehouseOptions: Array<{ id: string; name: string }>;
  orderOptions: OrderAutocompleteItem[];
  orderLoading?: boolean;
  itemOptions: Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>;
  uomOptionsByLineId?: Record<number, Array<{ id: string; name: string }>>;
  itemLoading?: boolean;
  stockByLineId?: Record<number, { on_hand_qty: number; allocated_qty: number; available_qty: number; uom_name?: string } | null>;
  stockLoadingByLineId?: Record<number, boolean>;
  stockErrorByLineId?: Record<number, boolean>;
  onChangeForm: (updates: Partial<InventoryIssueFormData>) => void;
  onChangeLine: (lineId: number, updates: Partial<InventoryIssueFormLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: number) => void;
  onSave: () => void;
  canEdit?: boolean;
  saving?: boolean;
  /** Only enable save after user has edited the form */
  isDirty?: boolean;
}

export default function IssueForm({
  formData,
  statusOptions,
  warehouseOptions,
  orderOptions,
  orderLoading = false,
  itemOptions,
  uomOptionsByLineId = {},
  itemLoading = false,
  stockByLineId = {},
  stockLoadingByLineId = {},
  stockErrorByLineId = {},
  onChangeForm,
  onChangeLine,
  onAddLine,
  onRemoveLine,
  onSave,
  canEdit = true,
  saving = false,
  isDirty = false,
}: IssueFormProps) {
  const readOnly = !canEdit;
  const selectedStatus = statusOptions.find((option) => option.id === formData.status) || undefined;

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={() => {}}
      submitting={saving}
      submitDisabled={saving || readOnly || !isDirty}
      submitLabel='Lưu phiếu xuất'
      submittingLabel='Đang lưu...'
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
      hideActionBar={false}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Thông tin phiếu xuất</Typography>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã phiếu xuất' labelWidth={100}>
              <TextField size='small' value={formData.code} onChange={(event) => onChangeForm({ code: event.target.value })} fullWidth disabled={readOnly} />
            </LabeledField>
            <LabeledField label='Đơn hàng' labelWidth={100}>
              <OrderAutocomplete
                orders={orderOptions}
                value={formData.order_id || null}
                onChange={(orderId) => onChangeForm({ order_id: orderId || '' })}
                placeholder='Chọn đơn hàng'
                disabled={readOnly}
                loading={orderLoading}
                fullWidth
                noOptionsText='Không tìm thấy đơn hàng'
                sx={sharedFormStyles.inputArrowAlign}
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho xuất' labelWidth={100}>
              <TextField
                select
                size='small'
                value={formData.store_id}
                onChange={(event) => onChangeForm({ store_id: event.target.value })}
                disabled={readOnly}
                fullWidth
                sx={sharedFormStyles.inputArrowAlign}
              >
                {warehouseOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </TextField>
            </LabeledField>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <Autocomplete
                disableClearable
                options={statusOptions}
                value={selectedStatus}
                onChange={(_event, newValue) => {
                  const nextStatus = newValue as { id: string; name: string } | null;
                  onChangeForm({ status: nextStatus?.id || '' });
                }}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={readOnly}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: 0, margin: 0 }}>
                    <Typography sx={{ color: option.color || 'inherit', paddingLeft: '24px', py: 0.5 }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn trạng thái'
                    size='small'
                    variant='standard'
                    InputProps={{
                      ...params.InputProps,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: selectedStatus?.color || 'inherit',
                        fontWeight: selectedStatus?.color ? 500 : 'inherit',
                      },
                    }}
                  />
                )}
                noOptionsText='Không tìm thấy trạng thái'
                fullWidth
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Danh sách vật tư xuất</Typography>
        <IssueLinesTable
          lines={formData.lines || []}
          itemOptions={itemOptions}
          uomOptionsByLineId={uomOptionsByLineId}
          itemLoading={itemLoading}
          stockByLineId={stockByLineId}
          stockLoadingByLineId={stockLoadingByLineId}
          stockErrorByLineId={stockErrorByLineId}
          onChangeLine={onChangeLine}
          onAddLine={onAddLine}
          onRemoveLine={onRemoveLine}
          disabled={readOnly}
        />
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Ghi chú</Typography>
        <TextareaAutosize
          minRows={4}
          value={formData.note}
          onChange={(event) => onChangeForm({ note: event.target.value })}
          disabled={readOnly}
          placeholder='Nhập ghi chú cho phiếu xuất...'
          style={sharedFormStyles.additionalTextarea(canEdit)}
        />
      </Paper>
    </AppForm>
  );
}
